import { Button, Icon } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { HUB_PAGE_SIZE } from "@/lib/queries";

/** Bottom of a group card: how many rows are loaded, and a button for the next page. */
export function OpportunityHubGroupFooter({
  shownCount,
  totalCount,
  hasMore,
  isLoading,
  isLegacy,
  onLoadMore,
}: {
  shownCount: number;
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  /** No-persona rows are topics rather than angles. */
  isLegacy: boolean;
  onLoadMore?: () => void;
}) {
  const { t } = useTranslation();
  const total = Math.max(totalCount, shownCount);
  const nextPageCount = Math.min(total - shownCount, HUB_PAGE_SIZE);
  const showing = isLegacy
    ? t("personaOpportunities.hub.opportunities.footer.showingOpportunities", {
        shown: shownCount,
        count: total,
        defaultValue_one: "Showing {{shown}} of {{count}} opportunity",
        defaultValue_other: "Showing {{shown}} of {{count}} opportunities",
      })
    : t("personaOpportunities.hub.opportunities.footer.showingAngles", {
        shown: shownCount,
        count: total,
        defaultValue_one: "Showing {{shown}} of {{count}} angle",
        defaultValue_other: "Showing {{shown}} of {{count}} angles",
      });

  return (
    <div className="flex items-center justify-between gap-3 bg-muted/20 px-4 py-2.5">
      <p className="text-xs tabular-nums text-muted-foreground">{showing}</p>
      {hasMore && onLoadMore ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={isLoading}
          aria-busy={isLoading || undefined}
          onClick={onLoadMore}
          className="h-auto gap-1.5 px-0 text-xs font-semibold"
        >
          {isLoading ? (
            <>
              <Icon
                name="loader"
                size="xs"
                color="inherit"
                className="animate-spin motion-reduce:animate-none"
                aria-hidden
              />
              {t("common.loading")}
            </>
          ) : nextPageCount > 0 ? (
            t("personaOpportunities.hub.opportunities.footer.loadCount", {
              count: nextPageCount,
              defaultValue: "Load {{count}} more",
            })
          ) : (
            t("personaOpportunities.hub.opportunities.footer.loadMore", "Load more")
          )}
        </Button>
      ) : null}
    </div>
  );
}
